/**
 * Pure computation for the chat unread-divider state and the first-load
 * scroll target.
 *
 * #1611 (original): added the "Unread" divider and the scroll-to-first-unread
 * behavior on first room load.
 *
 * #1861 (this fix): two production bugs reproduced at Jewel City on the
 * Lobby room.
 *
 *   A) The divider rendered ABOVE the user's OWN sent messages because the
 *      first-unread search filtered `temp-*` but not `type === 'self'`. A
 *      message the current user sent is, by definition, read by them — so
 *      it must never count as the unread anchor. Excluding own messages from
 *      candidates also makes the divider robust to the various ways the
 *      `markAsRead-on-send` path can fail to advance `lastReadAt` (iOS PWA
 *      localStorage eviction, dwell-timer cancellation by tab switch, send
 *      response missing a numeric `created_at`, etc).
 *
 *   B) On a fully-unread room with no older page available (small chat,
 *      `lastReadAt < oldestRendered`, `hasNextPage === false`), the previous
 *      logic set `firstUnreadId = messages[0].id` and the layout effect
 *      called `scrollIntoView({block:'start'})` on it — which landed the
 *      user at the top of the conversation. The fix: when the anchor is the
 *      oldest rendered message, prefer `scrollTarget = 'bottom'` so the
 *      user lands on the newest message (standard chat UX) and can scroll
 *      up to find the divider.
 */

export interface UnreadInputMessage {
  id: string;
  timestampSeconds?: number;
  type: 'self' | 'other';
}

export type ScrollTarget =
  | { kind: 'bottom' }
  | { kind: 'unread-anchor'; messageId: string };

export interface UnreadComputeInput {
  messages: UnreadInputMessage[];
  /**
   * Unix seconds of the user's last read on this device, or undefined if
   * the user has never marked this room read.
   */
  lastReadAt: number | undefined;
  /** Whether older pages exist beyond what is currently rendered. */
  hasNextPage: boolean;
}

export interface UnreadComputeResult {
  firstUnreadId: string | null;
  showDivider: boolean;
  scrollTarget: ScrollTarget;
}

export function computeUnreadState({
  messages,
  lastReadAt,
  hasNextPage,
}: UnreadComputeInput): UnreadComputeResult {
  if (!lastReadAt || messages.length === 0) {
    return {
      firstUnreadId: null,
      showDivider: false,
      scrollTarget: { kind: 'bottom' },
    };
  }

  const found = messages.find(
    (m) =>
      !m.id.startsWith('temp-') &&
      m.type !== 'self' &&
      (m.timestampSeconds ?? 0) > lastReadAt,
  );
  const firstUnreadId = found?.id ?? null;

  // Divider is reliable only when we have rendered far enough back to see
  // where lastReadAt actually falls. If hasNextPage is true and the oldest
  // rendered message is still newer than lastReadAt, the real boundary is
  // deeper in history — suppress the divider and land at bottom.
  const oldest = messages[0]?.timestampSeconds;
  const boundaryLoaded =
    !hasNextPage || (oldest !== undefined && oldest <= lastReadAt);

  const showDivider = boundaryLoaded && !!firstUnreadId;

  const scrollTarget: ScrollTarget =
    showDivider && firstUnreadId && firstUnreadId !== messages[0]?.id
      ? { kind: 'unread-anchor', messageId: firstUnreadId }
      : { kind: 'bottom' };

  return { firstUnreadId, showDivider, scrollTarget };
}
